/**
 * SB2-W-13 — derive the gateway Swagger URL from the env-resolved
 * `VITE_API_BASE_URL`.
 *
 * OQ-D3 (Christian, 2026-05-20):
 *   *"we do not build a /documentation page for now. While we do not
 *   have such a page, we route to SWAGGER documentation page:
 *   http://localhost:3005/api/docs."*
 *
 * The FE doesn't carry a dedicated `VITE_GATEWAY_BASE_URL` because the
 * three surface-specific base URLs (`VITE_API_BASE_URL`,
 * `VITE_AUTH_BASE_URL`, `VITE_ASSETS_BASE_URL`) all share the same
 * gateway origin under `realbe` mode. We extract that origin here so
 * the Documentation link tracks `realbe` / production overrides
 * without needing a new env var.
 *
 * Fallback chain (in order):
 *   1. `VITE_API_BASE_URL` origin   — e.g. `http://localhost:3005`
 *   2. `VITE_AUTH_BASE_URL` origin  — same gateway in `devapi-auth`
 *   3. `VITE_ASSETS_BASE_URL` origin — same gateway in `devapi-lyse`
 *   4. Same-origin relative `/api/docs` — last-ditch fallback when
 *      every env var is a relative path (`devapi-all` proxy mode).
 *
 * The path is always suffixed with `/api/docs` — that's where the
 * api-gateway mounts SwaggerUI (see `_BACKEND/services/api-gateway/src`).
 */
export function getSwaggerUrl(): string {
  const candidates = [
    (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? "",
    (import.meta.env.VITE_AUTH_BASE_URL as string | undefined) ?? "",
    (import.meta.env.VITE_ASSETS_BASE_URL as string | undefined) ?? "",
  ];
  for (const candidate of candidates) {
    if (!candidate) continue;
    try {
      // Absolute URL — strip path, keep origin. `new URL` throws for
      // relative paths (e.g. `/api/lyse/v1` in `devapi-all` mode),
      // which is why we wrap it.
      const u = new URL(candidate);
      return `${u.origin}/api/docs`;
    } catch {
      // Relative — fall through to the next candidate.
    }
  }
  // Every base URL was relative (devapi-all mode). Route to the
  // same-origin Swagger page; the Vite proxy on :5173 forwards `/api`
  // to the dev-api which does NOT mount SwaggerUI, so this is the
  // documented behaviour: in `devapi-all` mode there is no real BE
  // and therefore no Swagger. The link still points somewhere
  // meaningful rather than 404'ing on `/docs`.
  return "/api/docs";
}
